/** Loads a TracingImage's source and produces the cropped canvas the
 *  Scene places on the mat (issues #489, #500).
 *
 *  When a stored final copy exists (`finalUrl` — the de-skewed + cropped
 *  200-DPI render, #500) it's loaded directly: it's already rectified, so
 *  no warp is needed. Otherwise the raster is re-derived from the original
 *  `url` plus the crop window's preimage quad (normalized 0..1) and the
 *  physical size in mm — the same path that produces the final copy, so
 *  the warp can be re-rendered at any resolution.
 */

import { warpQuadToRect, type Quad } from "./keystone";
import type { TracingImage } from "../model/Project";

/** Sampling resolution for the placed image. 4 px/mm ≈ 100 dpi — plenty
 *  for an on-screen tracing reference without ballooning memory. */
const SCREEN_PX_PER_MM = 4;

/** Resolution for the stored final copy (issue #500). 200 dpi = 200 / 25.4
 *  px per mm ≈ 7.874 — a print-quality de-skewed + cropped render. */
const FINAL_PX_PER_MM = 200 / 25.4;

/** Long-side caps keep the per-pixel JS warp bounded for huge photos /
 *  huge physical sizes. The final copy gets a larger output cap so 200
 *  dpi actually holds for typical leather-piece sizes. */
const SOURCE_MAX_DIMENSION = 2000;
const SCREEN_OUTPUT_MAX_DIMENSION = 2000;
const FINAL_OUTPUT_MAX_DIMENSION = 4000;

/** Warp + crop `image` from its original source into a fresh canvas at
 *  `pxPerMm`, capped to `outputMax` on the long side. Shared by the
 *  on-screen reference render and the stored final copy. */
async function warpTracingImage(
  image: TracingImage,
  pxPerMm: number,
  outputMax: number,
): Promise<HTMLCanvasElement> {
  const res = await fetch(image.url);
  if (!res.ok) {
    throw new Error(`Image fetch failed (HTTP ${res.status})`);
  }
  const bitmap = await createImageBitmap(await res.blob());
  try {
    // Rasterize the source once, capped.
    const inScale = Math.min(
      1,
      SOURCE_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height),
    );
    const sw = Math.max(1, Math.round(bitmap.width * inScale));
    const sh = Math.max(1, Math.round(bitmap.height * inScale));
    const srcCanvas = document.createElement("canvas");
    srcCanvas.width = sw;
    srcCanvas.height = sh;
    const srcCtx = srcCanvas.getContext("2d")!;
    srcCtx.drawImage(bitmap, 0, 0, sw, sh);
    const src = srcCtx.getImageData(0, 0, sw, sh);

    // De-normalize the stored quad to the sampling raster.
    const quad = image.quad.map((p) => ({
      x: p.x * sw,
      y: p.y * sh,
    })) as Quad;

    const outScale = Math.min(
      1,
      outputMax / (pxPerMm * Math.max(image.widthMm, image.heightMm)),
    );
    const ow = Math.max(1, Math.round(image.widthMm * pxPerMm * outScale));
    const oh = Math.max(1, Math.round(image.heightMm * pxPerMm * outScale));
    const outCanvas = document.createElement("canvas");
    outCanvas.width = ow;
    outCanvas.height = oh;
    const outCtx = outCanvas.getContext("2d")!;
    const out = outCtx.createImageData(ow, oh);
    warpQuadToRect(src, quad, out);
    outCtx.putImageData(out, 0, 0);
    return outCanvas;
  } finally {
    bitmap.close();
  }
}

/** Decode an already-rectified image URL straight into a canvas at its
 *  natural size — no warp. Used for the stored final copy (issue #500),
 *  which is already de-skewed + cropped. */
async function decodeToCanvas(url: string): Promise<HTMLCanvasElement> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Image fetch failed (HTTP ${res.status})`);
  }
  const bitmap = await createImageBitmap(await res.blob());
  try {
    // Decode STRAIGHT DOWN to screen size (#959). The stored final copy is a
    // 200-dpi print-quality render capped at 4000px, and this is the only
    // consumer of it — the on-screen scene, where nothing above ~100 dpi is
    // visible. Decoded at source size it was a 16-megapixel bitmap that
    // paper.js re-drew, transformed, on every render: a full CPU resample per
    // frame while an image is dragged, which is the CPU hog in the report.
    //
    // The warp path below has always applied this cap. This fast path — the
    // common one, taken whenever a final copy exists — simply skipped it, so
    // the better-quality stored copy was the slower one to work with.
    const { width, height } = fitWithinMaxDimension(
      bitmap.width,
      bitmap.height,
      SCREEN_OUTPUT_MAX_DIMENSION,
    );
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d")!;
    // Let the browser do the downscale — it is a GPU/native path, unlike the
    // per-pixel JS warp, and `high` quality keeps a photographed pattern's
    // pencil lines legible at the reduced size.
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(bitmap, 0, 0, width, height);
    return canvas;
  } finally {
    bitmap.close();
  }
}

/**
 * Scale `w`×`h` down so its long side is at most `max`, preserving aspect.
 *
 * Never scales UP: a source already smaller than the cap is used as-is, since
 * enlarging it would cost memory and add no detail that isn't there.
 */
export function fitWithinMaxDimension(
  w: number,
  h: number,
  max: number,
): { width: number; height: number } {
  const longest = Math.max(w, h);
  if (longest <= max || longest === 0) return { width: w, height: h };
  const scale = max / longest;
  // At least 1px each way — a very long, very thin source must not round its
  // short side to a zero-width canvas, which throws on `drawImage`.
  return {
    width: Math.max(1, Math.round(w * scale)),
    height: Math.max(1, Math.round(h * scale)),
  };
}

export async function renderTracingImageCanvas(
  image: TracingImage,
): Promise<HTMLCanvasElement> {
  // Prefer the stored final copy (issue #500) — it's already the de-skewed
  // + cropped 200-DPI image, so decode it directly instead of re-running
  // the per-pixel warp on the original. Fall back to re-deriving from
  // `url` + `quad` when the copy is missing (not produced yet, logged-out
  // before it lands, upload failed) or can't be read.
  if (image.finalUrl) {
    try {
      return await decodeToCanvas(image.finalUrl);
    } catch {
      // fall through to the warp path
    }
  }
  return warpTracingImage(image, SCREEN_PX_PER_MM, SCREEN_OUTPUT_MAX_DIMENSION);
}

/** JPEG can't carry alpha — encoding a canvas with transparent pixels
 *  (unfilled warp margins, a PNG logo's background) would render them
 *  BLACK. Composite onto white first so they read as paper-white, which
 *  matches how a tracing photo sits on the mat. */
function compositeOnWhite(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const out = document.createElement("canvas");
  out.width = canvas.width;
  out.height = canvas.height;
  const ctx = out.getContext("2d")!;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, out.width, out.height);
  ctx.drawImage(canvas, 0, 0);
  return out;
}

/** Encode `canvas` trying each `(type, quality)` in order; the first
 *  encoding the browser actually produces wins (canvas.toBlob silently
 *  falls back to PNG for types it can't encode — detected by comparing
 *  the blob's real type). Include `image/jpeg` as the last attempt to
 *  guarantee a modern-enough floor: the HTML spec mandates the JPEG
 *  encoder in every browser, so with it in the list the trailing
 *  PNG-fallback return is effectively unreachable. */
async function encodeCanvas(
  canvas: HTMLCanvasElement,
  attempts: Array<[string, number]>,
): Promise<Blob> {
  let last: Blob | null = null;
  for (const [type, quality] of attempts) {
    const source = type === "image/jpeg" ? compositeOnWhite(canvas) : canvas;
    const blob = await new Promise<Blob | null>((resolve) =>
      source.toBlob((b) => resolve(b), type, quality),
    );
    if (blob?.type === type) return blob;
    last = blob ?? last;
  }
  if (!last) throw new Error("Image encode failed");
  return last;
}

/** Render the final copy (issue #500): the de-skewed + cropped region at
 *  200 dpi, encoded as AVIF where the browser's canvas can (Chrome,
 *  Safari), WebP next (#547 — Firefox can't encode AVIF but does WebP),
 *  and JPEG as the universal floor — a photo never ships as PNG.
 *  Returns the blob plus its actual MIME type so the caller uploads it
 *  with a matching content-type. */
export async function renderTracingImageFinal(
  image: TracingImage,
): Promise<{ blob: Blob; type: string }> {
  const canvas = await warpTracingImage(
    image,
    FINAL_PX_PER_MM,
    FINAL_OUTPUT_MAX_DIMENSION,
  );
  const blob = await encodeCanvas(canvas, [
    ["image/avif", 0.6],
    ["image/webp", 0.8],
    ["image/jpeg", 0.85],
  ]);
  return { blob, type: blob.type || "image/jpeg" };
}

/** Prepare a picked photo for the server (issue #547): PNG originals are
 *  re-encoded — WebP where the browser can, JPEG otherwise (Safari has
 *  no canvas WebP encoder, and the JPEG encoder is mandated everywhere;
 *  these are photos, so JPEG is the right widely-available default). A
 *  PNG photo/scan is typically several times the size of a visually-
 *  identical WebP/JPEG, and every downstream consumer
 *  (`warpTracingImage`) rasterizes the source capped at
 *  `SOURCE_MAX_DIMENSION` anyway, so the conversion also caps the long
 *  side there without losing anything the warp would ever sample.
 *  Already-compact formats (JPEG / WebP / AVIF / GIF) upload the file
 *  as-is. */
export async function prepareTracingUpload(
  file: File,
): Promise<{ body: Blob; type: string }> {
  if (file.type !== "image/png") return { body: file, type: file.type };
  try {
    const bitmap = await createImageBitmap(file);
    try {
      const scale = Math.min(
        1,
        SOURCE_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height),
      );
      const w = Math.max(1, Math.round(bitmap.width * scale));
      const h = Math.max(1, Math.round(bitmap.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d")!.drawImage(bitmap, 0, 0, w, h);
      const blob = await encodeCanvas(canvas, [
        ["image/webp", 0.92],
        ["image/jpeg", 0.92],
      ]);
      // Defensive: should some environment fail both encoders, fall
      // through to uploading the original untouched.
      if (blob.type === "image/webp" || blob.type === "image/jpeg") {
        return { body: blob, type: blob.type };
      }
    } finally {
      bitmap.close();
    }
  } catch {
    /* undecodable — let the server's own validation deal with it */
  }
  return { body: file, type: file.type };
}
